import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { ComissaoCadastroService } from '@/core/api/sicom-api/comissoes/cadastros/comissao-cadastro.service';
import { CctHistoricoResponse, CctMembroRequest, CctParticipacaoResponse, EstruturaRequest, EstruturaResponse } from '@/core/api/sicom-api/comissoes/cadastros/comissao-cadastro.model';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { ComissaoEstruturaFiltroResponse, ComissaoTipoComissaoResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { Toast } from '@/shared/components/toast/toast';
import { StepperModule } from 'primeng/stepper';
import { TagModule } from 'primeng/tag';
import { CardContent } from '@/shared/components/card-content/card-content';
import { InputMask } from 'primeng/inputmask';
import { SendWhatsapp } from '@/shared/components/send-whatsapp/send-whatsapp';
import * as presenter from './gerenciar-comissoes.presenter';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type AcaoGerenciarComissao = 'localizar' | 'novo' | 'editar' | 'salvar' | 'cancelar' | 'deletar' | 'extinguir';
type AbaComposicao = 'atual' | 'antiga';
type AcaoStatusComissao = 'extinguir' | 'reativar';
type TipoFiltroArvoreComissao = 'nome' | 'codigo';
type StatusFiltroArvoreComissao = 'todos' | 'ativas' | 'extintas';

interface TipoComissao {
    label: string;
    value: number;
}

interface ArvoreComissaoNo {
    id: number;
    idEoSuperior: number | null;
    descricao: string;
    ativo: number;
    filhos: ArvoreComissaoNo[];
}

interface ArvoreComissaoTipo {
    idTipoComissaoCt: number;
    descricao: string;
    filhos: ArvoreComissaoNo[];
}

interface MembroComissao {
    idSolicitante?: number;
    uf: string;
    idSolicitanteEe?: number | null;
    ufSgUfEe?: string | null;
    entidadeExterna?: string | null;
    tipoMembro?: string | null;
    crm?: string;
    nome: string;
    emails?: string;
    telefones?: string;
    avatar?: string | null;
    participacao?: string;
    dsParticipacao?: string;
    idParticipacao?: number;
    dtInicio?: string;
    dtFim?: string;
    nuPortariaInclusao?: string;
    nuPortariaRemocao?: string;
    assinouLgpd?: boolean;
    idStatusMembro?: number;
    composicaoAtual?: boolean;
    devedor?: boolean;
}

interface AfastamentoMembro {
    inicio: string;
    termino: string;
}

/** Estado do modal de edição/inclusão de integrante (formulário, distinto do `MembroComissao` exibido). */
interface MembroEdicaoForm {
    idSolicitante?: number | null;
    uf: string;
    idSolicitanteEe?: number | null;
    ufSgUfEe?: string | null;
    crm?: string;
    nome: string;
    entidadeExterna?: string;
    tipoMembro?: string;
    idParticipacao?: number;
    portariaEntradaData?: string;
    portariaEntradaNumero?: string;
    portariaSaidaData?: string;
    portariaSaidaNumero?: string;
    assinouLgpd?: boolean;
    isNew?: boolean;
}

interface HistoricoCct {
    data: string;
    evento: string;
    usuario: string;
}

interface FormComissao {
    estruturaSuperiorCodigo: string;
    estruturaSuperiorDescricao: string;
    codigo: string;
    nome: string;
    portariaCriacaoData: string;
    portariaCriacaoNumero: string;
    portariaExtincaoData: string;
    portariaExtincaoNumero: string;
    descricao: string;
    tipo: number | null;
    entidadeExternaCodigo: string;
    entidadeExternaUf: string;
    entidadeExternaNome: string;
}

@Component({
    selector: 'app-gerenciar-comissoes',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ButtonModule,
        CardModule,
        TooltipModule,
        CardContainer,
        InputTextModule,
        TextareaModule,
        SelectModule,
        TableModule,
        DialogModule,
        ConfirmDialogModule,
        SelectButtonModule,
        BotaoAcoes,
        Toast,
        StepperModule,
        TagModule,
        TableViewSwitcherComponent,
        CardContent,
        InputMask,
        SendWhatsapp,
        CanDirective
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './gerenciar-comissoes.html',
    styleUrl: './gerenciar-comissoes.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GerenciarComissoesComponent implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly estruturaService = inject(ComissaoCadastroService);
    private readonly comissaoReuniaoService = inject(ComissaoReuniaoService);
    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly cdr = inject(ChangeDetectorRef);
    private routeQueryParamsSub?: Subscription;
    private routeParamSub?: Subscription;

    protected readonly PERMISSOES = PERMISSOES;

    estado: 'visualizacao' | 'edicao' | 'insercao' = 'visualizacao';

    /**
     * Tela sem registro aberto: todos os campos ficam desabilitados e em branco, o que sem
     * um aviso explícito parecia um formulário quebrado em vez de um estado inicial.
     */
    get semComissaoCarregada(): boolean {
        return this.estado === 'visualizacao' && !this.form.codigo;
    }
    currentView = signal<'table' | 'card'>('card');
    wizardStep = 1;
    exibirDialogNovo = false;
    comissaoAtiva = true;
    acaoStatusPendente: AcaoStatusComissao | null = null;

    participacoesList: CctParticipacaoResponse[] = [];
    exibirDialogMembroDetalhes = false;
    membroEdicao: MembroEdicaoForm | null = null;

    readonly abasPrincipais = [
        { label: 'Membros', value: 'membros' },
        { label: 'Histórico da CCT', value: 'historico' }
    ];

    readonly abasComposicao = [
        { label: 'Composição Atual', value: 'atual' },
        { label: 'Composição Antiga', value: 'antiga' }
    ];

    abaPrincipal: 'membros' | 'historico' = 'membros';
    abaComposicao: AbaComposicao = 'atual';

    get itensAcoes(): AppMenuItem[] {
        const desabilitado = (id: AcaoGerenciarComissao) => this.botaoDesabilitado(id);
        return [
            {
                label: 'Localizar',
                icon: 'pi pi-search',
                styleClass: desabilitado('localizar') ? 'bg-gray-500' : 'bg-blue-500',
                disabled: desabilitado('localizar'),
                command: () => this.executarAcao('localizar')
            },
            {
                label: 'Novo',
                icon: 'pi pi-file',
                styleClass: desabilitado('novo') ? 'bg-gray-500' : 'bg-green-500',
                disabled: desabilitado('novo'),
                permission: PERMISSOES.comissoes.cadastro.criar,
                command: () => this.executarAcao('novo')
            },
            {
                label: 'Editar',
                icon: 'pi pi-pencil',
                styleClass: desabilitado('editar') ? 'bg-gray-500' : 'bg-orange-500',
                disabled: desabilitado('editar'),
                permission: PERMISSOES.comissoes.cadastro.editar,
                command: () => this.executarAcao('editar')
            },
            {
                label: 'Salvar',
                icon: 'pi pi-save',
                styleClass: desabilitado('salvar') ? 'bg-gray-500' : 'bg-indigo-500',
                disabled: desabilitado('salvar'),
                permission: this.estado === 'insercao' ? PERMISSOES.comissoes.cadastro.criar : PERMISSOES.comissoes.cadastro.editar,
                command: () => this.executarAcao('salvar')
            },
            {
                label: 'Cancelar',
                icon: 'pi pi-times-circle',
                styleClass: desabilitado('cancelar') ? 'bg-gray-500' : 'bg-red-500',
                disabled: desabilitado('cancelar'),
                permission: this.estado === 'insercao' ? PERMISSOES.comissoes.cadastro.criar : PERMISSOES.comissoes.cadastro.editar,
                command: () => this.executarAcao('cancelar')
            },
            {
                label: 'Deletar',
                icon: 'pi pi-trash',
                styleClass: desabilitado('deletar') ? 'bg-gray-500' : 'bg-red-500',
                disabled: desabilitado('deletar'),
                permission: PERMISSOES.comissoes.cadastro.excluir,
                command: () => this.executarAcao('deletar')
            },
            {
                label: this.comissaoAtiva ? 'Extinguir' : 'Reativar',
                icon: this.comissaoAtiva ? 'pi pi-lock' : 'pi pi-lock-open',
                styleClass: desabilitado('extinguir') ? 'bg-gray-500' : 'bg-purple-500',
                disabled: desabilitado('extinguir'),
                permission: PERMISSOES.comissoes.cadastro.alterarStatus,
                command: () => this.executarAcao('extinguir')
            }
        ];
    }

    tiposComissao: TipoComissao[] = [
        { label: 'Comissão', value: 1 },
        { label: 'Câmara Técnica', value: 2 },
        { label: 'Grupo de Trabalho', value: 3 }
    ];

    form: FormComissao = {
        estruturaSuperiorCodigo: '',
        estruturaSuperiorDescricao: '',
        codigo: '',
        nome: '',
        portariaCriacaoData: '',
        portariaCriacaoNumero: '',
        portariaExtincaoData: '',
        portariaExtincaoNumero: '',
        descricao: '',
        // Sem registro carregado o campo fica vazio: um tipo pré-selecionado se confunde
        // com dado real de uma comissão. O padrão só se aplica ao fluxo "Novo".
        tipo: null,
        entidadeExternaCodigo: '',
        entidadeExternaUf: '',
        entidadeExternaNome: ''
    };

    coordenadorId: number | null = null;
    coordenadorUf = '';
    coordenadorNome = '';

    membrosComposicaoAtual: MembroComissao[] = [];
    membrosComposicaoAntiga: MembroComissao[] = [];

    afastamentosMembro: AfastamentoMembro[] = [];
    membroSelecionado: MembroComissao | null = null;
    carregandoAfastamentos = false;

    expandedMembroId = signal<string | null>(null);

    toggleExpandMembro(event: Event, item: MembroComissao): void {
        event.stopPropagation();
        const id = (item.idSolicitante ?? item.crm ?? item.nome) + '-' + item.uf;
        if (this.expandedMembroId() === id) {
            this.expandedMembroId.set(null);
        } else {
            this.expandedMembroId.set(id);
        }
    }

    isMembroExpanded(item: MembroComissao): boolean {
        const id = (item.idSolicitante ?? item.crm ?? item.nome) + '-' + item.uf;
        return this.expandedMembroId() === id;
    }

    historicoCct: HistoricoCct[] = [];

    // Localizar Dialog
    exibirDialogLocalizar = false;
    filtroLocalizar = '';
    tipoFiltroLocalizar: TipoFiltroArvoreComissao = 'nome';
    statusFiltroLocalizar: StatusFiltroArvoreComissao = 'ativas';
    noLocalizarSelecionado: ArvoreComissaoNo | null = null;
    idsTiposLocalizarExpandidos = new Set<number>();
    idsNosLocalizarExpandidos = new Set<number>();

    // Participante Dialog
    exibirDialogParticipante = false;
    filtroParticipanteNome = '';
    carregandoParticipantes = false;
    participantesPesquisa: { idSolicitante: number; uf: string; nome: string }[] = [];
    tipoBuscaParticipante: 'coordenador' | 'membro' | 'entidadeExterna' | 'membroEntidadeExterna' = 'coordenador';

    // Superior Dialog
    exibirDialogSuperior = false;
    filtroSuperior = '';
    tipoFiltroSuperior: TipoFiltroArvoreComissao = 'nome';
    statusFiltroSuperior: StatusFiltroArvoreComissao = 'ativas';
    noSuperiorSelecionado: ArvoreComissaoNo | null = null;
    idsTiposSuperiorExpandidos = new Set<number>();
    idsNosSuperiorExpandidos = new Set<number>();

    arvoreComissoes: ArvoreComissaoTipo[] = [];

    get membrosExibidos(): MembroComissao[] {
        return this.abaComposicao === 'atual' ? this.membrosComposicaoAtual : this.membrosComposicaoAntiga;
    }

    get nomeMembroSelecionado(): string {
        return this.membroSelecionado?.nome || 'Nenhum integrante selecionado';
    }

    ngOnInit(): void {
        this.estruturaService.listarParticipacoes().subscribe((list) => {
            this.participacoesList = list;
            this.cdr.markForCheck();
        });
        this.carregarTiposComissao();

        this.routeParamSub = this.route.paramMap.subscribe((params) => {
            const codigo = params.get('codigo');
            if (codigo === 'membros' || codigo === 'historico') {
                this.abaPrincipal = codigo;
            } else {
                this.abaPrincipal = 'membros';
            }
            this.cdr.markForCheck();
        });

        this.routeQueryParamsSub = this.route.queryParams.subscribe((params) => {
            const idParam = params['id'];
            if (idParam) {
                const id = Number(idParam);
                if (Number.isFinite(id) && id > 0) {
                    this.carregarDadosComissao(id);
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.routeQueryParamsSub?.unsubscribe();
        this.routeParamSub?.unsubscribe();
    }

    private carregarTiposComissao(): void {
        this.comissaoReuniaoService.listarTiposComissao().subscribe({
            next: (tipos) => {
                const tiposMapeados = (tipos || [])
                    .filter((tipo) => !!tipo.idTipoComissaoCt)
                    .map((tipo) => ({
                        label: tipo.dsTipoComissaoCt || `Tipo ${tipo.idTipoComissaoCt}`,
                        value: tipo.idTipoComissaoCt as number
                    }));

                if (tiposMapeados.length > 0) {
                    this.tiposComissao = tiposMapeados;
                    if (!this.form.tipo) {
                        this.form.tipo = tiposMapeados[0].value;
                    }
                }
                this.cdr.markForCheck();
            }
        });
    }

    onAbaPrincipalChange(novaAba: 'membros' | 'historico'): void {
        const fragmentLabel = novaAba === 'membros' ? 'Membros' : 'Histórico da CCT';
        const targetRoute = this.router.url.includes('/item/') ? ['../', novaAba] : ['item', novaAba];
        this.router.navigate(targetRoute, {
            relativeTo: this.route,
            fragment: fragmentLabel,
            queryParamsHandling: 'preserve'
        });
    }

    botaoDesabilitado(id: AcaoGerenciarComissao): boolean {
        switch (id) {
            case 'localizar':
            case 'novo':
                return this.estado !== 'visualizacao';
            case 'editar':
                return this.estado !== 'visualizacao' || !this.form.codigo;
            case 'salvar':
                return this.estado === 'visualizacao' || !this.form.nome.trim();
            case 'cancelar':
                return this.estado === 'visualizacao';
            case 'deletar':
            case 'extinguir':
                return this.estado !== 'visualizacao' || !this.form.codigo;
            default:
                return false;
        }
    }

    carregarDadosComissao(id: number): void {
        this.estruturaService.buscarPorId(id).subscribe({
            next: (estrutura) => {
                this.form.codigo = String(estrutura.id);
                this.form.nome = estrutura.descricao;
                this.form.estruturaSuperiorCodigo = estrutura.idEoSuperior ? String(estrutura.idEoSuperior) : '';
                this.form.estruturaSuperiorDescricao = estrutura.nomeSuperior || '';
                this.form.descricao = estrutura.dsDescricao || estrutura.descricao || '';
                this.form.portariaCriacaoData = estrutura.dtPortariaInclusao ? presenter.formatarDataFrontend(estrutura.dtPortariaInclusao) : '';
                this.form.portariaCriacaoNumero = estrutura.nuPortariaInclusao || '';
                this.form.portariaExtincaoData = estrutura.dtPortariaRemocao ? presenter.formatarDataFrontend(estrutura.dtPortariaRemocao) : '';
                this.form.portariaExtincaoNumero = estrutura.nuPortariaRemocao || '';
                this.form.tipo = estrutura.idTipoComissaoCt ?? this.tiposComissao[0]?.value ?? 1;
                this.form.entidadeExternaCodigo = estrutura.idSolicitanteEe ? String(estrutura.idSolicitanteEe) : '';
                this.form.entidadeExternaUf = estrutura.ufSgUfEe || '';
                this.form.entidadeExternaNome = estrutura.entidadeExterna || '';
                this.comissaoAtiva = this.estruturaEstaAtiva(estrutura);
                this.carregarHistoricoCct(id, estrutura);

                this.coordenadorId = estrutura.idSolicitanteCoordenador;
                this.coordenadorUf = estrutura.sgUfCoordenador || '';
                this.coordenadorNome = estrutura.coordenador || '';

                this.membroSelecionado = null;
                this.afastamentosMembro = [];
                this.carregarComposicaoMembros(id);
                this.cdr.markForCheck();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar os dados da comissão.' });
                this.cdr.markForCheck();
            }
        });
    }

    private estruturaEstaAtiva(estrutura: EstruturaResponse): boolean {
        if (typeof estrutura.ativo === 'number') {
            return estrutura.ativo === 1;
        }
        return estrutura.inStatus !== 'I';
    }

    private carregarHistoricoCct(id: number, estrutura: EstruturaResponse): void {
        this.estruturaService.listarHistoricoCct(id).subscribe({
            next: (historico) => {
                this.historicoCct = this.mapearHistoricoCct(historico);
                if (this.historicoCct.length === 0) {
                    this.historicoCct = this.montarHistoricoLocal(estrutura);
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.historicoCct = this.montarHistoricoLocal(estrutura);
                this.cdr.markForCheck();
            }
        });
    }

    private mapearHistoricoCct(historico: CctHistoricoResponse[]): HistoricoCct[] {
        return (historico || []).map((item) => ({
            data: item.data ? this.formatarDataExibicao(item.data) || item.data : '',
            evento: item.evento || '',
            usuario: item.usuario || ''
        }));
    }

    private montarHistoricoLocal(estrutura: EstruturaResponse): HistoricoCct[] {
        const historico: HistoricoCct[] = [];

        if (estrutura.dtPortariaRemocao || estrutura.nuPortariaRemocao) {
            historico.push({
                data: estrutura.dtPortariaRemocao ? presenter.formatarDataFrontend(estrutura.dtPortariaRemocao) : '',
                evento: `Extinção pela portaria ${estrutura.nuPortariaRemocao || ''}`.trim(),
                usuario: ''
            });
        }

        if (estrutura.dtPortariaInclusao || estrutura.nuPortariaInclusao) {
            historico.push({
                data: estrutura.dtPortariaInclusao ? presenter.formatarDataFrontend(estrutura.dtPortariaInclusao) : '',
                evento: `Criação pela portaria ${estrutura.nuPortariaInclusao || ''}`.trim(),
                usuario: ''
            });
        }

        return historico;
    }

    private carregarComposicaoMembros(id: number): void {
        forkJoin({
            atuais: this.estruturaService.listarMembrosAtuais(id),
            antigos: this.estruturaService.listarMembrosAntigos(id)
        }).subscribe({
            next: ({ atuais, antigos }) => {
                this.membrosComposicaoAtual = atuais || [];
                this.membrosComposicaoAntiga = antigos || [];
                this.estado = 'visualizacao';
                this.exibirDialogNovo = false;
                this.selecionarPrimeiroMembroDisponivel();
                this.cdr.markForCheck();
            },
            error: () => {
                this.estruturaService.listarMembrosDetailed(id).subscribe({
                    next: (membros) => {
                        this.membrosComposicaoAtual = (membros || []).filter((m) => m.composicaoAtual !== false);
                        this.membrosComposicaoAntiga = (membros || []).filter((m) => m.composicaoAtual === false);
                        this.estado = 'visualizacao';
                        this.exibirDialogNovo = false;
                        this.selecionarPrimeiroMembroDisponivel();
                        this.cdr.markForCheck();
                    },
                    error: () => {
                        this.membrosComposicaoAtual = [];
                        this.membrosComposicaoAntiga = [];
                        this.membroSelecionado = null;
                        this.afastamentosMembro = [];
                        this.estado = 'visualizacao';
                        this.exibirDialogNovo = false;
                        this.cdr.markForCheck();
                    }
                });
            }
        });
    }

    selecionarAbaComposicao(aba: AbaComposicao): void {
        if (this.abaComposicao === aba) {
            return;
        }
        this.abaComposicao = aba;
        this.selecionarPrimeiroMembroDisponivel();
    }

    selecionarMembroComposicao(item: MembroComissao | null): void {
        this.membroSelecionado = item;
        this.afastamentosMembro = [];

        if (!item?.idSolicitante || !item.uf || !this.form.codigo) {
            return;
        }

        this.carregandoAfastamentos = true;
        this.estruturaService.listarHistoricoStatusMembro(Number(this.form.codigo), item.idSolicitante, item.uf).subscribe({
            next: (historico) => {
                this.afastamentosMembro = (historico || []).map((registro) => ({
                    inicio: registro.dtInicio ? this.formatarDataExibicao(registro.dtInicio) : '-',
                    termino: registro.dtFim ? this.formatarDataExibicao(registro.dtFim) : '-'
                }));
                this.carregandoAfastamentos = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.afastamentosMembro = [];
                this.carregandoAfastamentos = false;
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar os afastamentos do integrante.' });
                this.cdr.markForCheck();
            }
        });
    }

    isMembroSelecionado(item: MembroComissao): boolean {
        return !!this.membroSelecionado && presenter.chaveMembro(this.membroSelecionado) === presenter.chaveMembro(item);
    }

    private selecionarPrimeiroMembroDisponivel(): void {
        this.selecionarMembroComposicao(this.membrosExibidos[0] ?? null);
    }

    async copiarEmails(emails?: string): Promise<void> {
        const texto = emails?.trim();

        if (!texto) {
            this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Nenhum e-mail disponível para copiar.' });
            return;
        }

        try {
            await navigator.clipboard.writeText(texto);
            this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'E-mails copiados para a área de transferência.' });
        } catch {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar os e-mails.' });
        }
    }

    async copiarEmailsMembro(emails: string | undefined, event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const lista = this.obterListaEmails(emails);
        await this.copiarEmails(lista.join('; '));
    }

    async copiarTelefones(telefones?: string): Promise<void> {
        const texto = telefones?.trim();

        if (!texto) {
            this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Nenhum telefone disponível para copiar.' });
            return;
        }

        try {
            await navigator.clipboard.writeText(texto);
            this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'Telefone(s) copiado(s) para a área de transferência.' });
        } catch {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar os telefones.' });
        }
    }

    async copiarTelefonesMembro(telefones: string | undefined, event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const lista = this.obterListaTelefones(telefones);
        await this.copiarTelefones(lista.join('; '));
    }

    possuiEmails(emails?: string): boolean {
        return this.obterListaEmails(emails).length > 0;
    }

    possuiTelefones(telefones?: string): boolean {
        return this.obterListaTelefones(telefones).length > 0;
    }

    gerarMensagemWhatsappMembro(item: MembroComissao): string {
        const nomeComissao = this.form.nome || '';
        const participacao = item.dsParticipacao || item.participacao || '';
        const linhas = ['\u{1F465} *Contato de comissao*', item.nome ? `\u{1F464} Nome: ${item.nome}` : '', nomeComissao ? `\u{1F3DB}\uFE0F Comissao/CT: ${nomeComissao}` : '', participacao ? `\u{1F3F7}\uFE0F Participacao: ${participacao}` : ''];

        return linhas.filter((linha) => linha !== '').join('\n');
    }

    executarAcao(acao: AcaoGerenciarComissao): void {
        switch (acao) {
            case 'localizar':
                this.filtroLocalizar = '';
                this.tipoFiltroLocalizar = 'nome';
                this.statusFiltroLocalizar = 'ativas';
                this.noLocalizarSelecionado = null;
                this.exibirDialogLocalizar = true;
                this.carregarArvoreComissoes();
                break;

            case 'novo':
                this.form = {
                    estruturaSuperiorCodigo: '',
                    estruturaSuperiorDescricao: '',
                    codigo: '',
                    nome: '',
                    portariaCriacaoData: '',
                    portariaCriacaoNumero: '',
                    portariaExtincaoData: '',
                    portariaExtincaoNumero: '',
                    descricao: '',
                    tipo: this.tiposComissao[0]?.value ?? 1,
                    entidadeExternaCodigo: '',
                    entidadeExternaUf: '',
                    entidadeExternaNome: ''
                };
                this.comissaoAtiva = true;
                this.acaoStatusPendente = null;
                this.coordenadorId = null;
                this.coordenadorUf = '';
                this.coordenadorNome = '';
                this.membrosComposicaoAtual = [];
                this.membrosComposicaoAntiga = [];
                this.historicoCct = [];
                this.estado = 'insercao';
                this.wizardStep = 1;
                this.exibirDialogNovo = true;
                this.messageService.add({ severity: 'info', summary: 'Informação', detail: 'Inclusão de nova comissão iniciada.' });
                break;

            case 'editar':
                this.estado = 'edicao';
                this.messageService.add({ severity: 'info', summary: 'Informação', detail: 'Edição da comissão iniciada.' });
                break;

            case 'salvar':
                this.salvarComissao();
                break;

            case 'cancelar':
                this.acaoStatusPendente = null;
                if (this.estado === 'insercao') {
                    this.fecharDialogNovo();
                } else if (this.form.codigo) {
                    this.carregarDadosComissao(Number(this.form.codigo));
                } else {
                    this.estado = 'visualizacao';
                }
                this.messageService.add({ severity: 'info', summary: 'Informação', detail: 'Operação cancelada.' });
                break;

            case 'deletar':
                this.confirmationService.confirm({
                    message: `Deseja excluir a comissão ${this.form.nome}?`,
                    header: 'Confirmar exclusão',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Sim',
                    rejectLabel: 'Não',
                    acceptButtonStyleClass: 'p-button-danger',
                    accept: () => {
                        this.estruturaService.excluir(Number(this.form.codigo)).subscribe({
                            next: () => {
                                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Comissão excluída com sucesso.' });
                                this.form.codigo = '';
                                this.form.nome = '';
                                this.membrosComposicaoAtual = [];
                                this.estado = 'visualizacao';
                                this.cdr.markForCheck();
                            },
                            error: () => {
                                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível excluir a comissão.' });
                                this.cdr.markForCheck();
                            }
                        });
                    }
                });
                break;

            case 'extinguir':
                this.prepararAlteracaoStatus();
                break;
        }
    }

    private prepararAlteracaoStatus(): void {
        if (!this.form.codigo) return;

        const acao: AcaoStatusComissao = this.comissaoAtiva ? 'extinguir' : 'reativar';
        if (!this.validarPortariaStatus(acao)) {
            this.acaoStatusPendente = acao;
            this.estado = 'edicao';
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: acao === 'extinguir' ? 'Informe data e número da portaria de extinção antes de extinguir.' : 'Informe data e número da portaria de criação antes de reativar.'
            });
            return;
        }

        this.alterarStatusComPortaria(Number(this.form.codigo), acao);
    }

    private validarPortariaStatus(acao: AcaoStatusComissao): boolean {
        if (acao === 'extinguir') {
            return !!this.form.portariaExtincaoNumero.trim() && !!presenter.formatarDataBackend(this.form.portariaExtincaoData);
        }

        return !!this.form.portariaCriacaoNumero.trim() && !!presenter.formatarDataBackend(this.form.portariaCriacaoData);
    }

    private alterarStatusComPortaria(id: number, acao: AcaoStatusComissao): void {
        const request =
            acao === 'extinguir'
                ? {
                      nuPortaria: this.form.portariaExtincaoNumero.trim(),
                      dtPortaria: presenter.formatarDataBackend(this.form.portariaExtincaoData)
                  }
                : {
                      nuPortaria: this.form.portariaCriacaoNumero.trim(),
                      dtPortaria: presenter.formatarDataBackend(this.form.portariaCriacaoData)
                  };

        this.estruturaService.toggleStatus(id, request).subscribe({
            next: () => {
                this.acaoStatusPendente = null;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: acao === 'extinguir' ? 'Comissão extinta com sucesso.' : 'Comissão reativada com sucesso.'
                });
                this.carregarDadosComissao(id);
                this.cdr.markForCheck();
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: acao === 'extinguir' ? 'Não foi possível extinguir a comissão.' : 'Não foi possível reativar a comissão.'
                });
                this.cdr.markForCheck();
            }
        });
    }

    private salvarComissao(): void {
        if (!this.validarCadastroComissao()) {
            return;
        }

        const payload: EstruturaRequest = {
            id: this.form.codigo ? Number(this.form.codigo) : undefined,
            idEoSuperior: this.form.estruturaSuperiorCodigo ? Number(this.form.estruturaSuperiorCodigo) : null,
            descricao: this.form.nome.trim(),
            ativo: this.comissaoAtiva ? 1 : 0,
            idTipoComissaoCt: this.form.tipo,
            idSolicitanteCoordenador: this.coordenadorId,
            sgUfCoordenador: this.coordenadorUf || null,
            idSolicitanteEe: this.form.entidadeExternaCodigo ? Number(this.form.entidadeExternaCodigo) : null,
            ufSgUfEe: this.form.entidadeExternaUf || null,
            inCompartilharProtocolo: 'S',
            dsDescricao: this.form.descricao ? this.form.descricao.trim() : this.form.nome.trim(),
            nuPortariaInclusao: this.form.portariaCriacaoNumero || undefined,
            nuPortariaRemocao: this.form.portariaExtincaoNumero || undefined,
            dtPortariaInclusao: presenter.formatarDataBackend(this.form.portariaCriacaoData) || undefined,
            dtPortariaRemocao: presenter.formatarDataBackend(this.form.portariaExtincaoData) || undefined
        };

        const obs = this.estado === 'insercao' ? this.estruturaService.salvar(payload) : this.estruturaService.atualizar(Number(this.form.codigo), payload);

        obs.subscribe({
            next: (res) => {
                // If insercao, link accumulated members
                if (this.estado === 'insercao' && this.membrosComposicaoAtual.length > 0) {
                    this.vincularMembrosSequencial(res.id, 0);
                } else if (this.acaoStatusPendente) {
                    this.alterarStatusComPortaria(res.id, this.acaoStatusPendente);
                } else {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Comissão salva com sucesso.' });
                    this.carregarDadosComissao(res.id);
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível salvar os dados da comissão.' });
                this.cdr.markForCheck();
            }
        });
    }

    private validarCadastroComissao(): boolean {
        if (!this.form.nome.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe o nome da comissão.' });
            return false;
        }

        if (!this.form.estruturaSuperiorCodigo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe a estrutura superior.' });
            return false;
        }

        if (!this.form.tipo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe o tipo de comissão.' });
            return false;
        }

        if (!this.form.portariaCriacaoNumero.trim() || !presenter.formatarDataBackend(this.form.portariaCriacaoData)) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe data e número da portaria de criação.' });
            return false;
        }

        const temPortariaExtincao = !!this.form.portariaExtincaoNumero.trim() || !!this.form.portariaExtincaoData.trim();
        if (temPortariaExtincao && (!this.form.portariaExtincaoNumero.trim() || !presenter.formatarDataBackend(this.form.portariaExtincaoData))) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe data e número da portaria de extinção.' });
            return false;
        }

        if (this.acaoStatusPendente && !this.validarPortariaStatus(this.acaoStatusPendente)) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe a portaria obrigatória para alterar o status.' });
            return false;
        }

        return true;
    }

    private vincularMembrosSequencial(idEstrutura: number, index: number): void {
        if (index >= this.membrosComposicaoAtual.length) {
            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Comissão cadastrada com sucesso.' });
            this.carregarDadosComissao(idEstrutura);
            return;
        }

        const m = this.membrosComposicaoAtual[index];
        if (m.idSolicitante || m.idSolicitanteEe) {
            const reqPayload: CctMembroRequest = {
                idSolicitante: m.idSolicitante || null,
                uf: m.uf || null,
                idSolicitanteEe: m.idSolicitanteEe || null,
                ufSgUfEe: m.ufSgUfEe || null,
                idParticipacao: m.idParticipacao,
                dtInicio: m.dtInicio || presenter.formatarDataBackend(new Date().toLocaleDateString('pt-BR')) || undefined,
                nuPortariaInclusao: m.nuPortariaInclusao || undefined,
                dtFim: m.dtFim || undefined,
                nuPortariaRemocao: m.nuPortariaRemocao || undefined,
                assinouLgpd: m.assinouLgpd,
                validarDebitos: true
            };
            this.estruturaService.vincularMembroCct(idEstrutura, reqPayload).subscribe({
                next: () => this.vincularMembrosSequencial(idEstrutura, index + 1),
                error: () => this.vincularMembrosSequencial(idEstrutura, index + 1)
            });
        } else {
            this.vincularMembrosSequencial(idEstrutura, index + 1);
        }
    }

    cancelarTela(): void {
        const path = this.router.url.includes('/item/') ? ['../../dashboard'] : ['../dashboard'];
        this.router.navigate(path, { relativeTo: this.route });
    }

    // Arvore de comissoes
    private carregarArvoreComissoes(): void {
        forkJoin({
            tipos: this.comissaoReuniaoService.listarTiposComissao(),
            estruturas: this.comissaoReuniaoService.listarFiltrosComissao(0)
        }).subscribe({
            next: ({ tipos, estruturas }) => {
                this.arvoreComissoes = this.montarArvoreComissoes(tipos || [], estruturas || []);
                this.expandirRaizesArvoreLocalizar();
                this.expandirRaizesArvoreSuperior();
                this.cdr.markForCheck();
            },
            error: () => {
                this.arvoreComissoes = [];
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Nao foi possivel carregar a arvore de comissoes.' });
                this.cdr.markForCheck();
            }
        });
    }

    private montarArvoreComissoes(tipos: ComissaoTipoComissaoResponse[], estruturas: ComissaoEstruturaFiltroResponse[]): ArvoreComissaoTipo[] {
        const tiposValidos = tipos
            .filter((tipo) => this.normalizarIdPositivo(tipo.idTipoComissaoCt) !== null)
            .map((tipo) => ({
                idTipoComissaoCt: this.normalizarIdPositivo(tipo.idTipoComissaoCt) as number,
                descricao: tipo.dsTipoComissaoCt || `Tipo ${tipo.idTipoComissaoCt}`,
                filhos: [] as ArvoreComissaoNo[]
            }));

        const tiposPorId = new Map<number, ArvoreComissaoTipo>();
        tiposValidos.forEach((tipo) => tiposPorId.set(tipo.idTipoComissaoCt, tipo));

        const nosPorId = new Map<number, ArvoreComissaoNo>();
        const nosPorTipo = new Map<number, ArvoreComissaoNo[]>();

        estruturas.forEach((estrutura) => {
            const id = this.normalizarIdPositivo(estrutura.idEstruturaOrganizacional);
            const idTipo = this.normalizarIdPositivo(estrutura.idTipoComissaoCt);
            if (id === null || idTipo === null) {
                return;
            }

            if (!tiposPorId.has(idTipo)) {
                tiposPorId.set(idTipo, {
                    idTipoComissaoCt: idTipo,
                    descricao: `Tipo ${idTipo}`,
                    filhos: []
                });
            }

            const no: ArvoreComissaoNo = {
                id,
                idEoSuperior: this.normalizarIdPositivo(estrutura.idEoSuperior),
                descricao: estrutura.dsEstruturaOrganizacional || `Estrutura ${id}`,
                ativo: estrutura.ativo === 0 ? 0 : 1,
                filhos: []
            };

            nosPorId.set(id, no);
            const listaTipo = nosPorTipo.get(idTipo) || [];
            listaTipo.push(no);
            nosPorTipo.set(idTipo, listaTipo);
        });

        tiposPorId.forEach((tipo) => {
            const nosDoTipo = nosPorTipo.get(tipo.idTipoComissaoCt) || [];
            nosDoTipo.forEach((no) => {
                const pai = no.idEoSuperior ? nosPorId.get(no.idEoSuperior) : null;
                if (pai && nosDoTipo.includes(pai)) {
                    pai.filhos.push(no);
                } else {
                    tipo.filhos.push(no);
                }
            });
            this.ordenarNosArvore(tipo.filhos);
        });

        return Array.from(tiposPorId.values()).sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));
    }

    private ordenarNosArvore(nos: ArvoreComissaoNo[]): void {
        nos.sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));
        nos.forEach((no) => this.ordenarNosArvore(no.filhos));
    }

    private normalizarIdPositivo(valor: number | null | undefined): number | null {
        const id = Number(valor);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    private normalizarTexto(valor: string): string {
        return (
            valor
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                /* H\u00e1 nomes gravados com espa\u00e7o duplo; o HTML colapsa na exibi\u00e7\u00e3o, ent\u00e3o o usu\u00e1rio
               copia da tela um termo que nunca casaria com o valor cru. */
                .replace(/\s+/g, ' ')
                .trim()
        );
    }

    private filtrarTiposArvore(termo: string, tipoFiltro: TipoFiltroArvoreComissao, statusFiltro: StatusFiltroArvoreComissao): ArvoreComissaoTipo[] {
        return this.arvoreComissoes
            .map((tipo) => ({
                ...tipo,
                filhos: this.filtrarNosArvore(tipo.filhos, termo, tipoFiltro, statusFiltro)
            }))
            .filter((tipo) => tipo.filhos.length > 0 || !termo.trim());
    }

    private filtrarNosArvore(nos: ArvoreComissaoNo[], termo: string, tipoFiltro: TipoFiltroArvoreComissao, statusFiltro: StatusFiltroArvoreComissao): ArvoreComissaoNo[] {
        const busca = this.normalizarTexto(termo);
        return nos.reduce<ArvoreComissaoNo[]>((acc, no) => {
            const filhosFiltrados = this.filtrarNosArvore(no.filhos, termo, tipoFiltro, statusFiltro);
            const statusValido = this.statusNoValido(no, statusFiltro);
            const termoValido = !busca || (tipoFiltro === 'codigo' ? String(no.id).includes(busca) : this.normalizarTexto(no.descricao).includes(busca));

            if (statusValido && termoValido) {
                acc.push({ ...no, filhos: filhosFiltrados });
            } else if (filhosFiltrados.length > 0) {
                acc.push(...filhosFiltrados);
            }

            return acc;
        }, []);
    }

    private statusNoValido(no: ArvoreComissaoNo, statusFiltro: StatusFiltroArvoreComissao): boolean {
        if (statusFiltro === 'ativas') {
            return no.ativo === 1;
        }
        if (statusFiltro === 'extintas') {
            return no.ativo === 0;
        }
        return true;
    }

    get tiposLocalizarExibicao(): ArvoreComissaoTipo[] {
        return this.filtrarTiposArvore(this.filtroLocalizar, this.tipoFiltroLocalizar, this.statusFiltroLocalizar);
    }

    get tiposSuperiorExibicao(): ArvoreComissaoTipo[] {
        return this.filtrarTiposArvore(this.filtroSuperior, this.tipoFiltroSuperior, this.statusFiltroSuperior);
    }

    onFiltroLocalizarChange(valor: string): void {
        this.filtroLocalizar = this.tipoFiltroLocalizar === 'codigo' ? (valor || '').replace(/\D/g, '') : (valor || '').toUpperCase();
        this.expandirRaizesArvoreLocalizar();
    }

    onFiltroSuperiorChange(valor: string): void {
        this.filtroSuperior = this.tipoFiltroSuperior === 'codigo' ? (valor || '').replace(/\D/g, '') : (valor || '').toUpperCase();
        this.expandirRaizesArvoreSuperior();
    }

    alternarTipoLocalizar(idTipo: number): void {
        this.alternarSet(this.idsTiposLocalizarExpandidos, idTipo);
    }

    tipoLocalizarExpandido(idTipo: number): boolean {
        return this.idsTiposLocalizarExpandidos.has(idTipo);
    }

    alternarNoLocalizar(id: number): void {
        this.alternarSet(this.idsNosLocalizarExpandidos, id);
    }

    noLocalizarExpandido(id: number): boolean {
        return this.idsNosLocalizarExpandidos.has(id);
    }

    selecionarNoLocalizar(no: ArvoreComissaoNo): void {
        this.noLocalizarSelecionado = no;
    }

    confirmarLocalizar(): void {
        if (!this.noLocalizarSelecionado) {
            return;
        }
        this.exibirDialogLocalizar = false;
        this.carregarDadosComissao(this.noLocalizarSelecionado.id);
    }

    abrirBuscaSuperior(): void {
        this.filtroSuperior = '';
        this.tipoFiltroSuperior = 'nome';
        this.statusFiltroSuperior = 'ativas';
        this.noSuperiorSelecionado = null;
        this.exibirDialogSuperior = true;
        this.carregarArvoreComissoes();
    }

    alternarTipoSuperior(idTipo: number): void {
        this.alternarSet(this.idsTiposSuperiorExpandidos, idTipo);
    }

    tipoSuperiorExpandido(idTipo: number): boolean {
        return this.idsTiposSuperiorExpandidos.has(idTipo);
    }

    alternarNoSuperior(id: number): void {
        this.alternarSet(this.idsNosSuperiorExpandidos, id);
    }

    noSuperiorExpandido(id: number): boolean {
        return this.idsNosSuperiorExpandidos.has(id);
    }

    selecionarNoSuperior(no: ArvoreComissaoNo): void {
        this.noSuperiorSelecionado = no;
    }

    confirmarSuperior(): void {
        if (!this.noSuperiorSelecionado) {
            return;
        }
        this.exibirDialogSuperior = false;
        this.form.estruturaSuperiorCodigo = String(this.noSuperiorSelecionado.id);
        this.form.estruturaSuperiorDescricao = this.noSuperiorSelecionado.descricao;
    }

    private alternarSet(set: Set<number>, id: number): void {
        if (set.has(id)) {
            set.delete(id);
        } else {
            set.add(id);
        }
    }

    private expandirRaizesArvoreLocalizar(): void {
        if (this.filtroLocalizar.trim()) {
            this.idsTiposLocalizarExpandidos = new Set(this.arvoreComissoes.map((tipo) => tipo.idTipoComissaoCt));
            this.idsNosLocalizarExpandidos = new Set(this.coletarIdsComFilhos(this.tiposLocalizarExibicao));
        } else {
            this.idsTiposLocalizarExpandidos = new Set<number>();
            this.idsNosLocalizarExpandidos = new Set<number>();
        }
    }

    private expandirRaizesArvoreSuperior(): void {
        if (this.filtroSuperior.trim()) {
            this.idsTiposSuperiorExpandidos = new Set(this.arvoreComissoes.map((tipo) => tipo.idTipoComissaoCt));
            this.idsNosSuperiorExpandidos = new Set(this.coletarIdsComFilhos(this.tiposSuperiorExibicao));
        } else {
            this.idsTiposSuperiorExpandidos = new Set<number>();
            this.idsNosSuperiorExpandidos = new Set<number>();
        }
    }

    private coletarIdsComFilhos(tipos: ArvoreComissaoTipo[]): number[] {
        return tipos.flatMap((tipo) => this.coletarIdsNosComFilhos(tipo.filhos));
    }

    private coletarIdsNosComFilhos(nos: ArvoreComissaoNo[]): number[] {
        return nos.flatMap((no) => [...(no.filhos.length > 0 ? [no.id] : []), ...this.coletarIdsNosComFilhos(no.filhos)]);
    }

    trackTipoArvore(_: number, tipo: ArvoreComissaoTipo): number {
        return tipo.idTipoComissaoCt;
    }

    trackNoArvore(_: number, no: ArvoreComissaoNo): number {
        return no.id;
    }

    // Participante Helper
    abrirBuscaCoordenador(): void {
        this.tipoBuscaParticipante = 'coordenador';
        this.filtroParticipanteNome = '';
        this.participantesPesquisa = [];
        this.exibirDialogParticipante = true;
    }

    abrirBuscaMembro(): void {
        this.tipoBuscaParticipante = 'membro';
        this.filtroParticipanteNome = '';
        this.participantesPesquisa = [];
        this.exibirDialogParticipante = true;
    }

    abrirBuscaMembroEntidadeExterna(): void {
        this.tipoBuscaParticipante = 'membroEntidadeExterna';
        this.filtroParticipanteNome = '';
        this.participantesPesquisa = [];
        this.exibirDialogParticipante = true;
    }

    abrirBuscaEntidadeExterna(): void {
        this.tipoBuscaParticipante = 'entidadeExterna';
        this.filtroParticipanteNome = '';
        this.participantesPesquisa = [];
        this.exibirDialogParticipante = true;
    }

    pesquisarParticipantes(): void {
        const query = this.filtroParticipanteNome.trim();
        if (query.length < 3) return;

        this.carregandoParticipantes = true;
        this.estruturaService.buscarCoordenador(query).subscribe({
            next: (data) => {
                this.participantesPesquisa = (data.content || []).map((p) => ({
                    idSolicitante: p.idSolicitante,
                    uf: p.uf,
                    nome: p.nome
                }));
                this.carregandoParticipantes = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.participantesPesquisa = [];
                this.carregandoParticipantes = false;
                this.cdr.markForCheck();
            }
        });
    }

    selecionarParticipante(p: { idSolicitante: number; uf: string; nome: string }): void {
        this.exibirDialogParticipante = false;
        if (this.tipoBuscaParticipante === 'coordenador') {
            this.coordenadorId = p.idSolicitante;
            this.coordenadorUf = p.uf;
            this.coordenadorNome = p.nome;
        } else if (this.tipoBuscaParticipante === 'entidadeExterna') {
            this.form.entidadeExternaCodigo = String(p.idSolicitante);
            this.form.entidadeExternaUf = p.uf;
            this.form.entidadeExternaNome = p.nome;
        } else if (this.tipoBuscaParticipante === 'membroEntidadeExterna') {
            this.membroEdicao = {
                idSolicitante: null,
                uf: p.uf,
                idSolicitanteEe: p.idSolicitante,
                ufSgUfEe: p.uf,
                crm: '',
                nome: p.nome,
                entidadeExterna: p.nome,
                tipoMembro: 'ENTIDADE_EXTERNA',
                idParticipacao: this.participacoesList[0]?.id || 2,
                portariaEntradaData: '',
                portariaEntradaNumero: '',
                portariaSaidaData: '',
                portariaSaidaNumero: '',
                assinouLgpd: false,
                isNew: true
            };
            this.exibirDialogMembroDetalhes = true;
        } else {
            // Membro: open the details modal
            this.membroEdicao = {
                idSolicitante: p.idSolicitante,
                uf: p.uf,
                crm: '',
                nome: p.nome,
                idParticipacao: this.participacoesList[0]?.id || 2, // Default to Membro if available
                portariaEntradaData: '',
                portariaEntradaNumero: '',
                portariaSaidaData: '',
                portariaSaidaNumero: '',
                assinouLgpd: false,
                isNew: true
            };
            this.exibirDialogMembroDetalhes = true;
        }
    }

    removerMembro(membro: MembroComissao): void {
        if (this.estado === 'insercao') {
            this.membrosComposicaoAtual = this.membrosComposicaoAtual.filter((m) => presenter.chaveMembro(m) !== presenter.chaveMembro(membro));
        } else {
            if (membro.idSolicitante) {
                this.confirmationService.confirm({
                    message: `Deseja desvincular o integrante ${membro.nome}?`,
                    header: 'Confirmar desvinculação',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Sim',
                    rejectLabel: 'Não',
                    acceptButtonStyleClass: 'p-button-danger',
                    accept: () => {
                        const dtFim = membro.dtFim || presenter.formatarDataBackend(new Date().toLocaleDateString('pt-BR'));
                        this.estruturaService.encerrarMembroCct(Number(this.form.codigo), membro.idSolicitante!, membro.uf, dtFim, membro.nuPortariaRemocao || null).subscribe({
                            next: () => this.carregarDadosComissao(Number(this.form.codigo)),
                            error: () => this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível encerrar o vínculo do integrante.' })
                        });
                    }
                });
            }
        }
    }

    editarMembroDetalhes(item: MembroComissao): void {
        this.membroEdicao = {
            idSolicitante: item.idSolicitante,
            uf: item.uf,
            idSolicitanteEe: item.idSolicitanteEe || null,
            ufSgUfEe: item.ufSgUfEe || null,
            crm: item.crm,
            nome: item.nome,
            entidadeExterna: item.entidadeExterna || '',
            tipoMembro: item.tipoMembro || 'INTERNO',
            idParticipacao: item.idParticipacao || this.participacoesList[0]?.id || 2,
            portariaEntradaData: item.dtInicio ? presenter.formatarDataFrontend(item.dtInicio) : '',
            portariaEntradaNumero: item.nuPortariaInclusao || '',
            portariaSaidaData: item.dtFim ? presenter.formatarDataFrontend(item.dtFim) : '',
            portariaSaidaNumero: item.nuPortariaRemocao || '',
            assinouLgpd: item.assinouLgpd,
            isNew: false
        };
        this.exibirDialogMembroDetalhes = true;
    }

    private validarMembroEdicao(): boolean {
        const edicao = this.membroEdicao;
        if (!edicao) {
            return false;
        }

        if (!edicao.idParticipacao) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe a participação/cargo do integrante.' });
            return false;
        }

        if (!edicao.portariaEntradaNumero?.trim() || !presenter.formatarDataBackend(edicao.portariaEntradaData ?? '')) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe data e número da portaria de entrada.' });
            return false;
        }

        const temSaida = !!edicao.portariaSaidaNumero?.trim() || !!edicao.portariaSaidaData?.trim();
        if (temSaida && (!edicao.portariaSaidaNumero?.trim() || !presenter.formatarDataBackend(edicao.portariaSaidaData ?? ''))) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe data e número da portaria de saída.' });
            return false;
        }

        return true;
    }

    confirmarMembroDetalhes(): void {
        const edicao = this.membroEdicao;
        if (!edicao) return;

        if (!this.validarMembroEdicao()) {
            return;
        }

        const reqPayload: CctMembroRequest = {
            idSolicitante: edicao.idSolicitante,
            uf: edicao.uf,
            idSolicitanteEe: edicao.idSolicitanteEe || null,
            ufSgUfEe: edicao.ufSgUfEe || null,
            idParticipacao: edicao.idParticipacao,
            dtInicio: presenter.formatarDataBackend(edicao.portariaEntradaData ?? '') ?? undefined,
            nuPortariaInclusao: edicao.portariaEntradaNumero || undefined,
            dtFim: presenter.formatarDataBackend(edicao.portariaSaidaData ?? '') ?? undefined,
            nuPortariaRemocao: edicao.portariaSaidaNumero || undefined,
            assinouLgpd: edicao.assinouLgpd,
            validarDebitos: true
        };

        if (this.estado === 'insercao') {
            const jaExisteIndex = this.membrosComposicaoAtual.findIndex((m) => presenter.chaveMembro(m) === presenter.chaveMembro(edicao));

            const novoMembro: MembroComissao = {
                idSolicitante: edicao.idSolicitante ?? undefined,
                uf: edicao.uf,
                idSolicitanteEe: edicao.idSolicitanteEe || null,
                ufSgUfEe: edicao.ufSgUfEe || null,
                crm: edicao.crm,
                nome: edicao.nome,
                entidadeExterna: edicao.entidadeExterna || '',
                tipoMembro: edicao.tipoMembro || 'INTERNO',
                idParticipacao: edicao.idParticipacao,
                dsParticipacao: this.participacoesList.find((p) => p.id === edicao.idParticipacao)?.dsParticipacao || '',
                dtInicio: reqPayload.dtInicio,
                nuPortariaInclusao: reqPayload.nuPortariaInclusao,
                dtFim: reqPayload.dtFim,
                nuPortariaRemocao: reqPayload.nuPortariaRemocao,
                assinouLgpd: reqPayload.assinouLgpd,
                composicaoAtual: !reqPayload.dtFim,
                idStatusMembro: reqPayload.dtFim ? 1 : 0
            };

            if (jaExisteIndex !== -1) {
                this.membrosComposicaoAtual[jaExisteIndex] = novoMembro;
            } else {
                this.membrosComposicaoAtual.push(novoMembro);
            }
            this.exibirDialogMembroDetalhes = false;
        } else {
            this.estruturaService.vincularMembroCct(Number(this.form.codigo), reqPayload).subscribe({
                next: () => {
                    this.exibirDialogMembroDetalhes = false;
                    this.carregarDadosComissao(Number(this.form.codigo));
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível salvar os detalhes do integrante.' });
                    this.cdr.markForCheck();
                }
            });
        }
    }

    toggleAfastamento(item: MembroComissao): void {
        const actionLabel = item.idStatusMembro === 1 ? 'reativar' : 'afastar';
        this.confirmationService.confirm({
            message: `Deseja realmente ${actionLabel} o integrante ${item.nome}?`,
            header: actionLabel === 'reativar' ? 'Confirmar reativação' : 'Confirmar afastamento',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: actionLabel === 'reativar' ? 'p-button-success' : 'p-button-warning',
            accept: () => {
                const dataStatus = presenter.formatarDataBackend(new Date().toLocaleDateString('pt-BR'));
                this.estruturaService.registrarAfastamento(Number(this.form.codigo), item.idSolicitante!, item.uf, dataStatus || undefined).subscribe({
                    next: () => this.carregarDadosComissao(Number(this.form.codigo)),
                    error: () => this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível registrar o afastamento/reativação.' })
                });
            }
        });
    }

    formatarDataExibicao(data: string): string {
        return presenter.formatarDataFrontend(data);
    }

    fecharDialogNovo(): void {
        this.exibirDialogNovo = false;
        if (this.estado === 'insercao') {
            this.estado = 'visualizacao';
            if (this.form.codigo) {
                const cod = Number(this.form.codigo);
                if (Number.isFinite(cod) && cod > 0) {
                    this.carregarDadosComissao(cod);
                } else {
                    this.resetarForm();
                }
            } else {
                this.resetarForm();
            }
        }
    }

    formatarUf(uf?: string | null): string {
        return presenter.formatarUf(uf);
    }

    obterBandeiraUf(uf?: string | null): string | null {
        return presenter.obterBandeiraUf(uf);
    }

    obterEstiloParticipacao(item: MembroComissao): { background: string; color: string } {
        const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('app-dark');
        const desc = (item?.dsParticipacao || item?.participacao || '').trim().toUpperCase();
        if (desc.includes('COORDENADOR') && !desc.includes('VICE')) {
            return isDark ? { background: 'rgba(59, 130, 246, 0.16)', color: '#60a5fa' } : { background: 'var(--p-blue-100)', color: 'var(--p-blue-700)' };
        } else if (desc.includes('VICE')) {
            return isDark ? { background: 'rgba(168, 85, 247, 0.16)', color: '#c084fc' } : { background: 'var(--p-purple-100)', color: 'var(--p-purple-700)' };
        } else if (desc.includes('EFETIVO') && desc.includes('INTERNO')) {
            return isDark ? { background: 'rgba(20, 184, 166, 0.16)', color: '#2dd4bf' } : { background: 'var(--p-teal-100)', color: 'var(--p-teal-700)' };
        } else if (desc.includes('EFETIVO') && desc.includes('EXTERNO')) {
            return isDark ? { background: 'rgba(99, 102, 241, 0.16)', color: '#818cf8' } : { background: 'var(--p-indigo-100)', color: 'var(--p-indigo-700)' };
        } else if (desc.includes('EFETIVO')) {
            return isDark ? { background: 'rgba(6, 182, 212, 0.16)', color: '#22d3ee' } : { background: 'var(--p-cyan-100)', color: 'var(--p-cyan-700)' };
        } else if (desc.includes('AFASTADO')) {
            return isDark ? { background: 'rgba(239, 68, 68, 0.16)', color: '#f87171' } : { background: 'var(--p-red-100)', color: 'var(--p-red-700)' };
        } else if (desc.includes('MEMBRO')) {
            return isDark ? { background: 'rgba(34, 197, 94, 0.16)', color: '#4ade80' } : { background: 'var(--p-green-100)', color: 'var(--p-green-700)' };
        } else if (desc.includes('SECRET')) {
            return isDark ? { background: 'rgba(249, 115, 22, 0.16)', color: '#fb923c' } : { background: 'var(--p-orange-100)', color: 'var(--p-orange-700)' };
        } else if (desc.includes('SUPLENTE')) {
            return isDark ? { background: 'rgba(245, 158, 11, 0.16)', color: '#fbbf24' } : { background: 'var(--p-amber-100)', color: 'var(--p-amber-700)' };
        }
        return isDark ? { background: 'rgba(156, 163, 175, 0.16)', color: '#d1d5db' } : { background: 'var(--p-surface-100)', color: 'var(--p-surface-700)' };
    }

    iniciaisMembro(nome: string): string {
        return presenter.iniciaisMembro(nome);
    }

    obterListaEmails(emails?: string): string[] {
        return presenter.obterListaEmails(emails);
    }

    obterListaTelefones(telefones?: string): string[] {
        return presenter.obterListaTelefones(telefones);
    }

    private resetarForm(): void {
        this.form = {
            estruturaSuperiorCodigo: '',
            estruturaSuperiorDescricao: '',
            codigo: '',
            nome: '',
            portariaCriacaoData: '',
            portariaCriacaoNumero: '',
            portariaExtincaoData: '',
            portariaExtincaoNumero: '',
            descricao: '',
            tipo: null,
            entidadeExternaCodigo: '',
            entidadeExternaUf: '',
            entidadeExternaNome: ''
        };
        this.comissaoAtiva = true;
        this.acaoStatusPendente = null;
        this.coordenadorId = null;
        this.coordenadorUf = '';
        this.coordenadorNome = '';
        this.membrosComposicaoAtual = [];
        this.membrosComposicaoAntiga = [];
        this.historicoCct = [];
    }
}
